import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MarkdownRenderer from './shared/MarkdownRenderer';

export default function AIResponseDetailsModal({ isOpen, onClose, message, metadata }) {
  const navigate = useNavigate();
  const [selectedFunctionIdx, setSelectedFunctionIdx] = useState(0);
  const [expandedCollections, setExpandedCollections] = useState(new Set());
  const [expandedItems, setExpandedItems] = useState(new Set());

  const hasFunctionCalls = metadata?.function_calls && metadata.function_calls.length > 0;
  const hasSources = metadata?.sources && metadata.sources.length > 0;

  // Sort sources by ranking (lower rank = better match)
  const sortedSources = metadata?.sources ? [...metadata.sources].sort((a, b) => {
    const rankA = a.collection?.rank || a.collection_rank || Infinity;
    const rankB = b.collection?.rank || b.collection_rank || Infinity;
    return rankA - rankB;
  }) : [];

  // Handle inspect button click
  const handleInspect = () => {
    if (!sortedSources || sortedSources.length === 0) return;

    // Create collection filters from sources
    const collectionFilters = sortedSources.map((source, idx) => ({
      id: Date.now() + idx,
      type: 'ai-response-collection',
      collectionId: source.collection_id,
      collectionTitle: source.collection?.title || 'Unknown Collection',
      rank: source.collection?.rank || source.collection_rank || idx + 1
    }));

    // Close modal
    onClose();

    // Navigate to knowledge page with filters
    navigate('/knowledge', {
      state: {
        collectionFilters,
        fromAIResponse: true
      }
    });
  };

  // Auto-select first function call when modal opens
  useEffect(() => {
    if (isOpen && hasFunctionCalls) {
      setSelectedFunctionIdx(0);
    }
  }, [isOpen, hasFunctionCalls]);

  const toggleCollection = (idx) => {
    const newSet = new Set(expandedCollections);
    if (newSet.has(idx)) {
      newSet.delete(idx);
    } else {
      newSet.add(idx);
    }
    setExpandedCollections(newSet);
  };

  const toggleItem = (key) => {
    const newSet = new Set(expandedItems);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedItems(newSet);
  };

  if (!isOpen) return null;

  const selectedFunction = hasFunctionCalls ? metadata.function_calls[selectedFunctionIdx] : null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">AI Response Details</h2>
              <p className="text-sm text-gray-400 mt-1">Function calls and knowledge sources</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700/50 rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Response Info Header */}
        <div className="bg-gradient-to-r from-purple-900/20 via-gray-800/40 to-gray-800/40 border-b border-purple-500/20 px-6 py-3" style={{ color: 'var(--color-text-on-accent)' }}>
                  <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center" style={{ color: 'var(--color-text-on-accent)' }}>
                              <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="bg-gray-800/40 border border-gray-600/40 rounded-lg px-3 py-2 flex items-center">
                <div className="text-sm text-gray-200 line-clamp-2 w-full">
                  <MarkdownRenderer content={message} readOnly={true} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* Function Calls */}
          {hasFunctionCalls && (
            <div className="border border-blue-500/20 rounded-lg p-4 bg-blue-500/5">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <div className="w-5 h-5 rounded bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                  <svg className="w-3 h-3 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                Function Calls
                <span className="ml-auto text-xs font-normal text-gray-500">
                  {metadata.function_calls.length} call{metadata.function_calls.length !== 1 ? 's' : ''}
                </span>
              </h3>

              {/* Function Pills */}
              <div className="flex flex-wrap gap-2 mb-3">
                {metadata.function_calls.map((call, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedFunctionIdx(idx)}
                    className={`px-3 py-1.5 rounded-full font-mono text-xs font-semibold transition-all duration-200 ${
                      selectedFunctionIdx === idx
                        ? 'bg-gradient-to-r from-blue-600 to-blue-500 shadow-lg shadow-blue-500/30 border border-blue-400/50'
                        : 'bg-gray-800/80 text-gray-300 hover:bg-gray-700/80 border border-gray-600/50 hover:border-gray-500/50'
                    }`}
                    style={selectedFunctionIdx === idx ? { color: 'var(--color-text-on-accent)' } : {}}
                  >
                    {call.name}()
                  </button>
                ))}
              </div>

              {/* Selected Function Details */}
              {selectedFunction && selectedFunction.arguments && Object.keys(selectedFunction.arguments).length > 0 && (
                <div className="bg-gradient-to-br from-gray-800/40 to-gray-800/60 border border-gray-600/50 rounded-lg p-3" style={{ color: 'var(--color-text-on-accent)' }}>
                                  {/* Arguments */}
                  <div>
                    <div className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-2">Arguments</div>
                    <div className="bg-gray-900/70 border border-gray-700/50 rounded-lg p-3 space-y-2">
                      {Object.entries(selectedFunction.arguments).map(([key, value]) => (
                        <div key={key}>
                          <div className="text-xs font-semibold text-blue-300 mb-1 font-mono">{key}</div>
                          <div className="bg-black/30 rounded px-3 py-2 border border-gray-700/30">
                            {typeof value === 'object' ? (
                              <pre className="font-mono text-xs text-gray-200 overflow-x-auto">{JSON.stringify(value, null, 2)}</pre>
                            ) : (
                              <span className="text-sm text-gray-200 font-mono">{String(value)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Knowledge Sources */}
          {hasSources && (
            <div className="border border-emerald-500/20 rounded-lg p-4 bg-emerald-500/5">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <div className="w-5 h-5 rounded bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                  <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                Knowledge Sources
                <span className="ml-auto text-xs font-normal text-gray-500">
                  {sortedSources.length} collection{sortedSources.length !== 1 ? 's' : ''}
                </span>
              </h3>
              <div className="space-y-2">
                {sortedSources.map((source, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-gray-800/40 to-gray-800/60 border border-gray-600/50 rounded-lg overflow-hidden" style={{ color: 'var(--color-text-on-accent)' }}>
                                      {/* Collection Header - Clickable */}
                    <button
                      onClick={() => toggleCollection(idx)}
                      className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-800/70 transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${expandedCollections.has(idx) ? 'rotate-90' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <div className="flex-shrink-0 w-6 h-6 bg-emerald-500/10 border border-emerald-500/30 rounded flex items-center justify-center">
                          <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <div className="text-sm font-semibold text-emerald-300 truncate">
                            {source.collection?.title || 'Unknown Collection'}
                          </div>
                          <div className="text-[11px] text-gray-400">
                            {source.collection?.category && (
                              <span className="inline-flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/50"></span>
                                {source.collection.category}
                                <span className="mx-1">•</span>
                              </span>
                            )}
                            {source.items?.length || 0} item{source.items?.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-2 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded text-xs font-semibold text-emerald-300">
                        #{source.collection?.rank || source.collection_rank || idx + 1}
                      </div>
                    </button>

                    {/* Items List - Expandable */}
                    {expandedCollections.has(idx) && source.items && source.items.length > 0 && (
                      <div className="px-3 pb-3 pt-2 border-t border-gray-700/50 bg-gray-900/50">
                        <div className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-2">Referenced Items</div>
                        <div className="space-y-2">
                          {source.items.map((item, itemIdx) => {
                            const itemKey = `${idx}-${itemIdx}`;
                            return (
                              <div key={itemIdx} className="bg-black/30 border border-gray-700/40 rounded-lg overflow-hidden">
                                {/* Item Header - Clickable */}
                                <button
                                  onClick={() => toggleItem(itemKey)}
                                  className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-800/40 transition-colors text-left"
                                >
                                  <div className="flex-1 min-w-0 flex items-center gap-2">
                                    <svg
                                      className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${expandedItems.has(itemKey) ? 'rotate-90' : ''}`}
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                    <svg className="w-3.5 h-3.5 text-emerald-400/60 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <div className="text-sm text-gray-200 truncate font-medium">{item.title}</div>
                                  </div>
                                  <div className="flex-shrink-0 ml-2 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded text-xs font-semibold text-emerald-300">
                                    #{item.rank || itemIdx + 1}
                                  </div>
                                </button>

                                {/* Item Content - Expandable */}
                                {expandedItems.has(itemKey) && item.content && (
                                  <div className="px-3 pb-3 pt-2 border-t border-gray-800/50">
                                    <div className="bg-black/40 rounded-lg p-3 max-h-64 overflow-y-auto border border-gray-800/30">
                                      <p className="text-xs text-gray-300 leading-relaxed whitespace-pre-wrap">{item.content}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Metadata Message */}
          {!hasFunctionCalls && !hasSources && (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-800/30 border-2 border-gray-700/50 mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-200 font-medium mb-2">No Additional Details</p>
              <p className="text-sm text-gray-400">This response didn't use any function calls or knowledge sources</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 bg-gray-800/60 flex justify-end gap-3">
          {hasSources && (
            <button
              onClick={handleInspect}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Inspect
            </button>
          )}
          <button
            onClick={onClose}
            className="px-5 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl text-sm"
            style={{ color: 'var(--color-text-on-accent)' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
